import Decimal from "decimal.js";

import {
  BuyBackIndicatorEnum,
  RealizedIndicatorEnum,
  WashSaleIndicatorEnum,
} from "../common";
import { BUSINESS_TIMEZONE, DateOnly } from "../date_utils";
import { DirectIndexType, OrderSide } from "../generated/graphql";
import { _findReplacementLots } from "../portfolio_utils";
import { Percentage, ZERO } from "../utils";

/**
 * We use cost instead of openBuyPrice since cost includes wash sales adjusted amount
 */
export function percentageChangeInCost<
  T extends {
    quantity: Decimal;
    cost: Decimal;
  },
>(taxLot: T, currentPrice: Decimal): Decimal {
  const result = taxLot.quantity
    .mul(currentPrice)
    .minus(taxLot.cost)
    .div(taxLot.cost)
    .toDecimalPlaces(5);
  return result;
}

/**
 * Tax lot is candidate for sell only if
 * - we get a realized loss >= lossFactor%
 * - it doesn't lead to wash sale
 */
export function geCandidatesWithRealizableLoss<
  T extends {
    id: string;
    securityId: string;
    quantity: Decimal;
    cost: Decimal;
    realizedIndicator: RealizedIndicatorEnum;
    washSalesIndicator: WashSaleIndicatorEnum;
    buyBackIndicator: BuyBackIndicatorEnum;
    realizedGainLoss: Decimal;
    taxLotOpenBuyDate?: DateOnly;
    taxLotCloseSellDate?: DateOnly;
    eventTime: Date;
  },
>(
  securityId: string,
  currentPrice: Decimal,
  date: DateOnly,
  taxLots: T[],
  lossFactor: Percentage,
  avoidWashSale: boolean,
): T[] {
  const taxLossHarvestableLots = taxLots.filter(
    (taxLot) =>
      taxLot.realizedIndicator === RealizedIndicatorEnum.Unrealized &&
      taxLot.securityId === securityId &&
      percentageChangeInCost(taxLot, currentPrice).lte(
        lossFactor.getRate().neg(),
      ),
  );

  const qtySold = taxLossHarvestableLots.reduce(
    (acc, t) => acc.add(t.quantity),
    ZERO,
  );

  if (qtySold.equals(0)) {
    return [];
  }
  const sellLots = new Set(taxLossHarvestableLots.map((t) => t.id));

  const anyReplacementLots = _findReplacementLots(
    {
      securityId: securityId,
      side: OrderSide.Sell,
      quantity: qtySold,
      eventTime: date.toDateStartOfDay(BUSINESS_TIMEZONE),
    },
    taxLots.filter((t) => !sellLots.has(t.id)),
  );

  return avoidWashSale && anyReplacementLots.length > 0
    ? []
    : taxLossHarvestableLots;
}

/**
 * Calculates the tax loss harvest potential for the first year of direct indexing,
 * accounting for the remaining months in the year.
 * This is currently hardcoded to reflect the results of our whitepaper.
 */
export const calculateTaxLossHarvestPotential = (
  directIndexType: DirectIndexType,
  sellValue: Decimal,
) => {
  if (
    directIndexType !== DirectIndexType.Sp500 &&
    directIndexType !== DirectIndexType.CrspLargeCap
  ) {
    return ZERO;
  }

  // For S&P 500 / CRSP large cap / SP Info Tech: 14.7% is the loss harvest potential for the first year of direct indexing
  const lhpFactor = new Decimal("0.147");
  // range of remainingMonths: 1 - 12
  const remainingMonths = new Decimal(
    12 - DateOnly.now(BUSINESS_TIMEZONE).getMonthNumber(),
  );

  return sellValue.times(lhpFactor).times(remainingMonths.div(12));
};
